import { pageLinkOf } from '@padcanvas/shared';
import type { Destination, PublishedLink } from '../api/publishedLinksApi';

/** How a published link reads in the admin console — shared by the list, the
 *  detail page and the form. */

export const PUBLIC_BASE = 'https://buzzbrain.ai';

/**
 * The URL a link is published as. A link is served on the host of its
 * destination (target §6.1): a zitmus page link reads `<host>/<page…>/<source>`,
 * any other site link `<its host>/p/<slug>`, everything else the student app's.
 */
export function publicUrl(l: Pick<PublishedLink, 'slug' | 'destination'>): string {
  const d = l.destination;
  const origin = d.kind === 'site' ? (() => { try { return new URL(d.url).origin; } catch { return PUBLIC_BASE; } })() : PUBLIC_BASE;
  const page = pageLinkOf(l);
  return page ? `${origin}/${page.page}/${page.source}` : `${origin}/p/${l.slug}`;
}

export const shortUrl = (url: string) => url.replace(/^https:\/\//, '');

export type DestinationKind = Destination['kind'];

export const DESTINATION_KINDS: { value: DestinationKind; label: string }[] = [
  { value: 'chapter-practice', label: 'Chapter practice' },
  { value: 'question-set', label: 'Question set' },
  { value: 'simulation', label: 'Simulation' },
  { value: 'route', label: 'App page' },
  { value: 'site', label: 'Our site' },
];

export const TARGET_LABEL: Record<DestinationKind, string> = {
  'chapter-practice': 'Question set ID',
  'question-set': 'Question set ID',
  simulation: 'Simulation ID',
  route: 'App path',
  site: 'URL',
};

export const TARGET_PLACEHOLDER: Record<DestinationKind, string> = {
  'chapter-practice': 'qs-…',
  'question-set': 'qs-…',
  simulation: 'projectile-motion',
  route: '/practice',
  site: 'https://zitmus.buzzbrain.ai/optics-bench/',
};

export const publisherLabel = (l: PublishedLink) =>
  (l.publisher.kind === 'partner' ? l.publisher.id : l.publisher.kind === 'us' ? 'Us' : 'Student');

export const destinationLabel = (d: Destination) => {
  switch (d.kind) {
    case 'chapter-practice': return `Chapter${d.chapterNumber ? ` ${d.chapterNumber}` : ''}`;
    case 'question-set': return 'Question set';
    case 'simulation': return `Simulation · ${d.simulationId}`;
    case 'route': return `App · ${d.path}`;
    case 'site': return `Site · ${d.url}`;
  }
};

export const destinationTarget = (d: Destination) =>
  ('questionSetId' in d ? d.questionSetId : 'simulationId' in d ? d.simulationId : 'path' in d ? d.path : d.url);

/** A load time as people say it: `2.4 s`, or `—` when nothing was measured. */
export const fmtLoad = (ms?: number) => (ms === undefined ? '—' : `${(ms / 1000).toFixed(1)} s`);

/** Loads as a share of opens — `—` when there were no opens to share. */
export const loadRate = (l: Pick<PublishedLink, 'opens' | 'loads'>) =>
  (l.opens ? `${Math.round(((l.loads ?? 0) / l.opens) * 100)}%` : '—');
